/* jshint esversion: 6 */
/***********************************************************************
 Factory to create a specific course
 ***********************************************************************/
import { LoggingService as logger } from "@/logging/LoggingService";
import { Assert } from "@/common/Assert";
import { createCmi5Course } from "@/cmi5/createCmi5Course";
import { createAiccCourse } from "@/aicc/createAiccCourse";
import { createScormCourse } from "@/scorm/createScormCourse";
import { createPublisherCourse } from "@/publisher/createPublisherCourse";
import { createLocalStorageCourse } from "@/localstorage/createLocalStorageCourse";
import { createPortalCourseOptionsProvider } from "./createPortalCourseOptionsProvider";
//logger.enableDebug();
const logId = "CourseFactory";
export function CourseFactory() {
    const factoryFunctions = [
        (portalOptionsProvider) => createCmi5Course(portalOptionsProvider.getCmi5Options()),
        (portalOptionsProvider) => createAiccCourse(portalOptionsProvider.getAiccOptions()),
        (portalOptionsProvider) => createScormCourse(portalOptionsProvider.getScormOptions()),
        (portalOptionsProvider) => createPublisherCourse(portalOptionsProvider.getPublisherOptions()),
        (portalOptionsProvider) => createLocalStorageCourse(portalOptionsProvider.getLocalStorageOptions())
    ];
    // revealing public api
    return {
        createCourse: function (constructorOptions) {
            const optionsProvider = createPortalCourseOptionsProvider(constructorOptions);
            if (optionsProvider.isCoursePreview()) {
                try {
                    return createLocalStorageCourse(optionsProvider.getLocalStorageOptions()) || null;
                }
                catch (error) {
                    logger.error(`cannot create course for preview: ${error}`, logId);
                }
            }
            // first valid created course wins, see factoryFunctions for order.
            let lastError;
            for (let i = 0; i < factoryFunctions.length; i++) {
                try {
                    const course = factoryFunctions[i](optionsProvider);
                    if (course) {
                        return course;
                    }
                }
                catch (error) {
                    lastError = error;
                }
            }
            logger.error(`cannot create course: ${lastError}`, logId);
            return null;
        },
        // Legacy factory function for portal configurations < 23.1
        createAiccCourse: (aiccOptions, scormOptions, courseOptions) => {
            try {
                Assert.checkArgument(aiccOptions);
                Assert.checkArgument(courseOptions);
                return createAiccCourse({
                    proxy: (typeof aiccOptions.sAiccProxy === "string") ? aiccOptions.sAiccProxy : undefined,
                    masteryScore: (typeof courseOptions.nMasteryScore === "number") ? courseOptions.nMasteryScore : undefined,
                    aiccURL: (typeof aiccOptions.sAiccURL === "string") ? aiccOptions.sAiccURL : undefined,
                    aiccSID: (typeof aiccOptions.sAiccSID === "string") ? aiccOptions.sAiccSID : undefined
                }) || null;
            }
            catch (error) {
                logger.error(`cannot create scorm course: ${error}`, logId);
            }
            return null;
        },
        createScormCourse: (lmsApi, scormOptions) => {
            try {
                Assert.checkArgument(scormOptions);
                return createScormCourse({
                    lmsApi,
                    scoExportType: (scormOptions.sScoExportType === "singleSco") ? "singleSco" : "multiSco",
                    scoExportVersion: scormOptions.sScoExportVersion,
                    masteryScore: (typeof scormOptions.nMasteryScore === "number") ? scormOptions.nMasteryScore : undefined,
                }) || null;
            }
            catch (error) {
                logger.error(`cannot create scorm course: ${error}`, logId);
            }
            return null;
        },
        createAcceleratorCourse: (lmsQuery, courseOptions) => {
            try {
                Assert.checkArgument(lmsQuery);
                Assert.checkArgument(courseOptions);
                Assert.checkState(typeof courseOptions.sCourseLanguage === "string");
                Assert.checkState(typeof courseOptions.sCourseLanguage === "string");
                return createPublisherCourse({
                    lmsQuery,
                    userIsLoggedIn: true,
                    courseId: courseOptions.sCourseCompactId,
                    courseLanguage: courseOptions.sCourseLanguage,
                    masteryScore: courseOptions.nMasteryScore ? courseOptions.nMasteryScore : undefined
                }) || null;
            }
            catch (error) {
                logger.error(`cannot create portal course: ${error}`, logId);
            }
            return null;
        },
        createCookieCourse: (userId, courseOptions) => {
            try {
                Assert.checkArgument(userId);
                Assert.checkArgument(courseOptions);
                return createLocalStorageCourse({
                    userId,
                    masteryScore: courseOptions.nMasteryScore ? courseOptions.nMasteryScore : undefined
                });
            }
            catch (error) {
                logger.error(`cannot create local storage course: ${error}`, logId);
            }
            return null;
        }
    };
}
